// In dentlab/src/hooks/useFileUpload.ts
import { useState, useCallback } from "react";

export interface FileUploadHandlers {
	onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
	onDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
	onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
	onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}

export interface UseFileUploadReturn {
	isDragging: boolean;
	isUploading: boolean;
	dragHandlers: FileUploadHandlers;
	setIsUploading: (uploading: boolean) => void;
}

export interface UseFileUploadOptions {
	onFileUpload: (files: File[]) => void;
	enableFolderUpload?: boolean; // Add this option
}

/**
 * Hook for handling drag-and-drop file uploads
 * Extracts reusable drag-and-drop logic for file uploads
 */
export const useFileUpload = ({
	onFileUpload,
	enableFolderUpload = false, // Default to false for backward compatibility
}: UseFileUploadOptions): UseFileUploadReturn => {
	const [isDragging, setIsDragging] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDragEnter = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			if (e.dataTransfer.types.includes("Files")) {
				setIsDragging(true);
			}
		},
		[]
	);

	const handleDragLeave = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			// Only set dragging to false if we're leaving the drop zone entirely
			const rect = e.currentTarget.getBoundingClientRect();
			const x = e.clientX;
			const y = e.clientY;
			if (
				x < rect.left ||
				x >= rect.right ||
				y < rect.top ||
				y >= rect.bottom
			) {
				setIsDragging(false);
			}
		},
		[]
	);

	// Enhanced function to process dropped items (files and folders)
	const processDroppedItems = useCallback(
		async (items: DataTransferItemList) => {
			const files: File[] = [];

			// Process each dropped item
			for (let i = 0; i < items.length; i++) {
				const item = items[i];

				if (item.kind === "file") {
					// Check if it's a directory
					if (enableFolderUpload && item.webkitGetAsEntry) {
						const entry = item.webkitGetAsEntry();
						if (entry && entry.isDirectory) {
							// Recursively get all files from the directory
							const dirFiles = await getFilesFromDirectory(entry);
							files.push(...dirFiles);
						} else if (entry && entry.isFile) {
							// It's a file
							const file = item.getAsFile();
							if (file) files.push(file);
						}
					} else {
						// Fallback for browsers without webkitGetAsEntry
						const file = item.getAsFile();
						if (file) files.push(file);
					}
				}
			}

			return files;
		},
		[enableFolderUpload]
	);

	// Recursive function to get all files from a directory
	const getFilesFromDirectory = useCallback(
		async (entry: any): Promise<File[]> => {
			const files: File[] = [];

			if (entry.isFile) {
				return new Promise((resolve) => {
					entry.file((file: File) => {
						resolve([file]);
					});
				});
			} else if (entry.isDirectory) {
				const reader = entry.createReader();
				const entries: any[] = [];
				let batch: any[] = [];
				do {
					batch = await new Promise<any[]>((resolve, reject) => {
						reader.readEntries(resolve, reject);
					});
					entries.push(...batch);
				} while (batch.length > 0);

				for (const childEntry of entries) {
					const childFiles = await getFilesFromDirectory(childEntry);
					files.push(...childFiles);
				}
			}

			return files;
		},
		[]
	);

	const handleDrop = useCallback(
		async (e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);

			// Try to get files from items first (for folder support)
			let files: File[] = [];

			if (enableFolderUpload && e.dataTransfer.items) {
				files = await processDroppedItems(e.dataTransfer.items);
			} else {
				// Fallback to the original method
				files = Array.from(e.dataTransfer.files);
			}

			if (files.length === 0) return;

			setIsUploading(true);
			await onFileUpload(files);
			setIsUploading(false);
		},
		[onFileUpload, enableFolderUpload, processDroppedItems]
	);

	const dragHandlers: FileUploadHandlers = {
		onDragOver: handleDragOver,
		onDragEnter: handleDragEnter,
		onDragLeave: handleDragLeave,
		onDrop: handleDrop,
	};

	return {
		isDragging,
		isUploading,
		dragHandlers,
		setIsUploading,
	};
};
